import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { SearchResult } from '@/components/shared/SearchBox';
import { useRelevanceSearch } from '@/features/relevanceSearch/api/getRelevanceSearch';
import type { RelevanceSearchResult } from '@/features/relevanceSearch/types/RelevanceSearchResult';
import {
  resolveDestinationFromSearchInput,
} from '@/features/relevanceSearch/utils/resolveRelevanceSearchDestination';
import useAppointmentStore from '@/store/AppointmentStore';
import useContextAwarenessStore from '@/store/ContextAwarenessStore';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import type { FlowType } from '@/types/FlowType';
import { blastDataToLogCannon } from '@/utils/blastDataToLogCannon';
import useProceedWithDestination from './useProceedWithDestination';

const buildDeepLinkSearchResults = (
  relevanceResults: RelevanceSearchResult[],
): SearchResult[] => {
  if (!relevanceResults.length) {
    return [];
  }

  return relevanceResults.map((result) => ({
    id: result.id,
    title: result.name,
    icon: 'location-dot-duotone' as const,
    subtitle:
      result.type?.includes('country') || result.country === 'Unknown'
        ? undefined
        : [result.region, result.country].filter(Boolean).join(', '),
  }));
};

const getNormalizedQueryParam = (searchParams: URLSearchParams, key: string) =>
  searchParams.get(key)?.trim() ?? '';

type DeepLinkResolutionStatus = 'idle' | 'resolving' | 'done';

// Single owner of context-awareness URL params:
//   - card / sourceUrl are persisted as marketing attribution
//   - ?dest= is resolved against destinations and proceeds straight into
//     scheduling; callers render a loader while `isResolving` is true.
const useContextAwarenessParams = (flow: FlowType) => {
  const [searchParams] = useSearchParams();
  const hasStoredAttribution = useRef(false);
  const hasLoggedContextAwareness = useRef(false);

  const setContextAwareness = useContextAwarenessStore(
    (state) => state.setContextAwareness,
  );
  const setSelectedDestination = useAppointmentStore(
    (state) => state.setSelectedDestination,
  );
  const setSearchedDestination = useAppointmentStore(
    (state) => state.setSearchedDestination,
  );
  const setUserSearch = useAppointmentStore((state) => state.setUserSearch);
  const destinations = useAppointmentStore((state) => state.destinations);
  const useNewDestinations = useFeatureFlagsStore(
    (state) => state.featureFlags.useNewDestinations || false,
  );
  const proceedWithDestination = useProceedWithDestination(flow);

  const destinationQuery = useMemo(
    () => getNormalizedQueryParam(searchParams, 'dest'),
    [searchParams],
  );
  const [deepLinkResolutionStatus, setDeepLinkResolutionStatus] =
    useState<DeepLinkResolutionStatus>(() =>
      destinationQuery ? 'resolving' : 'idle',
    );

  const contextAwareness = useMemo(
    () => ({
      card: getNormalizedQueryParam(searchParams, 'card'),
      sourceUrl: getNormalizedQueryParam(searchParams, 'sourceUrl'),
    }),
    [searchParams],
  );

  const relevanceSearchQuery = useRelevanceSearch(destinationQuery);

  // Log context awareness parameters for Splunk tracking
  useEffect(() => {
    if (hasLoggedContextAwareness.current) return;

    if (
      contextAwareness.card ||
      contextAwareness.sourceUrl ||
      destinationQuery
    ) {
      hasLoggedContextAwareness.current = true;
      blastDataToLogCannon(
        {
          card: contextAwareness.card,
          sourceUrl: contextAwareness.sourceUrl,
          dest: destinationQuery,
        },
        'TRVL-CONTEXT-AWARENESS',
      );
    }
  }, [contextAwareness.card, contextAwareness.sourceUrl, destinationQuery]);

  const isResolving = deepLinkResolutionStatus === 'resolving';

  useEffect(() => {
    if (hasStoredAttribution.current) return;
    hasStoredAttribution.current = true;
    // Don't wipe stored values when remounting on a route without params.
    if (!contextAwareness.card && !contextAwareness.sourceUrl) return;
    setContextAwareness(contextAwareness);
  }, [contextAwareness, setContextAwareness]);

  useEffect(() => {
    if (destinationQuery && deepLinkResolutionStatus === 'idle') {
      setDeepLinkResolutionStatus('resolving');
    }
  }, [deepLinkResolutionStatus, destinationQuery]);

  useEffect(() => {
    if (deepLinkResolutionStatus !== 'resolving' || !destinationQuery) return;
    if (relevanceSearchQuery.isPending || !destinations.length) return;

    const relevanceResults = relevanceSearchQuery.data ?? [];
    const resolvedDestination = resolveDestinationFromSearchInput({
      typedSearch: destinationQuery,
      relevanceResults,
      searchResults: buildDeepLinkSearchResults(relevanceResults),
      destinations,
      useNewDestinations,
    });

    if (!resolvedDestination) {
      setDeepLinkResolutionStatus('done');
      return;
    }

    const { selectedDestination, searchedDestination, userSearch } =
      resolvedDestination;

    setUserSearch(userSearch ?? '');
    setSearchedDestination(searchedDestination);
    setSelectedDestination(selectedDestination);
    proceedWithDestination(selectedDestination);
  }, [
    deepLinkResolutionStatus,
    destinationQuery,
    destinations,
    proceedWithDestination,
    relevanceSearchQuery.data,
    relevanceSearchQuery.isPending,
    setSearchedDestination,
    setSelectedDestination,
    setUserSearch,
    useNewDestinations,
  ]);

  return { isResolving };
};

export default useContextAwarenessParams;
